import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Grid } from '@mui/material';
import { runScenario,
  fetchCashflow,
  fetchMetrics,
  fetchMetricsMode } from '../api';
import {
  useNotification,
  isPermissionDenied,
  getPermissionDeniedMessage,
  notifyPermissionError,
} from '@1823-partners/core';
import PortfolioForm from './PortfolioForm';
import CusipTables from './CusipTables';
import { notifyUngatedFailure } from '../utils/notifyDenial';

const PortfolioAnalysis = ({ userId, prefilledData, onDataUsed }) => {
  const { showNotification } = useNotification();

  // const [formData, setFormData] = useState({
  //   cusips: ['110331PN9','60637BFA3'],
  //   asofdt: '2025-03-31',
  //   env: 'UAT',
  //   scenarios: ['US0001'],
  // });

  // Effect to handle prefilled data
  useEffect(() => {
    if (prefilledData) {
      setFormData(prefilledData);
      // Call onDataUsed to clear the prefilled data after using it
      if (onDataUsed) {
        onDataUsed();
      }
    }
  }, [prefilledData, onDataUsed]);

  const [formData, setFormData] = useState({
    id: null,
    cusips: [],
    asofdt: '',
    env: 'UAT',
    scenarios: [],
    description: '',
  });


  const [cashflowData, setCashflowData] = useState([]);
  const [metricsData, setMetricsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [currentConfigId, setCurrentConfigId] = useState(null);
  const [error, setError] = useState(null);
  // Which flow's failure the banner is currently showing. Inputs stay editable
  // during a run, so editing them starts a fresh fetchPastResults *after*
  // submission — one that invalidateActiveFetch below cannot have invalidated.
  // If the run then fails, that fetch succeeding would clear the run's message
  // out from under the user. A fetch may clear its own message; it may not clear
  // the run's.
  const errorSourceRef = useRef(null);

  // One setter for the banner, so every path that reports a failure goes through
  // the same place. No denial object rides along: /cashflow and /metrics carry no
  // @require_permission, so a permission_denied from them would mean an auth or
  // proxy problem rather than a missing role, and there would be no role to
  // request. Only save_configs, run_scenario and upload_scenarios are gated, and
  // those report through notifyPermissionError, which attaches its own action.
  const showError = (message, source = null) => {
    setError(message);
    errorSourceRef.current = message ? source : null;
  };
  const [isFormComplete, setIsFormComplete] = useState(false);
  const [isWaitingForConfig, setIsWaitingForConfig] = useState(false);
  const [metricsMode, setMetricsMode] = useState(true); // Default to true
  const [selectedLotIds, setSelectedLotIds] = useState([]); // LOT_ID filter
  
  // Only the newest fetchPastResults may write shared state. The effect below
  // re-fires on every formData change, so requests overlap: without this, an
  // older denial can land after a newer success and leave a Request access
  // button naming a role for a portfolio the user is no longer looking at.
  const requestIdRef = useRef(0);

  // Abandon whatever fetch is in flight. The spinner has to be cleared here
  // rather than left to that request's finally: it now sees a newer id and
  // skips its own cleanup, so with no replacement fetch the grids would spin
  // indefinitely.
  const invalidateActiveFetch = () => {
    requestIdRef.current += 1;
    setTableLoading(false);
  };

  const pollingIntervalRef = useRef(null);

  // Extract unique LOT_IDs from both datasets
  const availableLotIds = useMemo(() => {
    const lotIdSet = new Set();
    
    cashflowData.forEach(row => {
      if (row.LOT_ID != null) {
        lotIdSet.add(String(row.LOT_ID));
      }
    });
    
    metricsData.forEach(row => {
      if (row.LOT_ID != null) {
        lotIdSet.add(String(row.LOT_ID));
      }
    });
    
    return Array.from(lotIdSet).sort();
  }, [cashflowData, metricsData]);

  // Filter data based on selected LOT_IDs
  const filteredCashflowData = useMemo(() => {
    if (selectedLotIds.length === 0) return cashflowData;
    return cashflowData.filter(row => selectedLotIds.includes(String(row.LOT_ID)));
  }, [cashflowData, selectedLotIds]);

  const filteredMetricsData = useMemo(() => {
    if (selectedLotIds.length === 0) return metricsData;
    return metricsData.filter(row => selectedLotIds.includes(String(row.LOT_ID)));
  }, [metricsData, selectedLotIds]);

  // Enhanced data cleaning function
  const cleanDataForGrid = (data) => {
    if (!Array.isArray(data)) {
      console.warn('Data is not an array:', typeof data, data);
      return [];
    }

    console.log('Cleaning data, original length:', data.length);
    
    const cleaned = data.map((row, index) => {
      const cleanRow = {};
      
      Object.keys(row).forEach(key => {
        let value = row[key];
        
        // Handle different types of "invalid" values
        if (value === null || value === undefined) {
          cleanRow[key] = null;
        } else if (typeof value === 'number' && (isNaN(value) || !isFinite(value))) {
          cleanRow[key] = null;
        } else if (typeof value === 'string') {
          // Handle string representations of NaN or null
          const trimmed = value.trim();
          if (trimmed === 'NaN' || trimmed === 'null' || trimmed === 'undefined' || trimmed === '') {
            cleanRow[key] = null;
          } else {
            cleanRow[key] = value;
          }
        } else {
          cleanRow[key] = value;
        }
      });
      
      // Debug first few rows
      if (index < 3) {
        console.log(`Row ${index} cleaned:`, {
          CUSIP: cleanRow.CUSIP,
          CLEAN_PX: cleanRow.CLEAN_PX,
          BOOK_VALUE: cleanRow.BOOK_VALUE,
          GL: cleanRow.GL
        });
      }
      
      return cleanRow;
    });

    console.log('Data cleaned, final length:', cleaned.length);
    return cleaned;
  };

  // Watch for form completion
  useEffect(() => {
    console.log('Form data changed:', formData);
    const isComplete = formData.cusips && 
                      formData.cusips.length > 0 &&
                      formData.asofdt && 
                      formData.scenarios.length > 0;
    
    console.log('Is form complete?', isComplete);
    setIsFormComplete(isComplete);
    
    if (isComplete) {
      console.log('Form complete, fetching past results...');
      fetchPastResults(isComplete);
    } else {
      console.log('Form incomplete, clearing data');
      // Clear data if form becomes incomplete. The error goes too: it described
      // inputs the user has since changed, and a stale denial would keep
      // offering a Request access button for them.
      setCashflowData([]);
      setMetricsData([]);
      showError(null);
      invalidateActiveFetch();
    }
  }, [formData.cusips, formData.asofdt, formData.scenarios, formData.env]);

  const fetchPastResults = async (isComplete) => {
    console.log('fetchPastResults called, isComplete:', isComplete);
    if (!isComplete) {
      console.log('Form not complete, returning early');
      return;
    }
    
    const payload = {
      cusips: formData.cusips,
      scenarios: formData.scenarios,
      asofdt: formData.asofdt,
      env: formData.env
    };
    
    console.log('Fetching past results with payload:', payload);

    const requestId = ++requestIdRef.current;
    setTableLoading(true);
    try {
      console.log('Making API calls with payload:', payload);
      const [cashflowResponse, metricsResponse] = await Promise.all([
        fetchCashflow(payload),
        fetchMetrics(payload)
      ]);

      console.log('API Response received:');
      console.log('- Cashflow data length:', cashflowResponse?.data?.length || 0);
      console.log('- Metrics data length:', metricsResponse?.data?.length || 0);
      console.log('- Metrics response type:', typeof metricsResponse?.data);
      console.log('- Metrics is array:', Array.isArray(metricsResponse?.data));

      // Log raw metrics data sample
      if (metricsResponse?.data && metricsResponse.data.length > 0) {
        console.log('Raw metrics first record:', metricsResponse.data[0]);
        console.log('Raw metrics data sample values:');
        const firstRecord = metricsResponse.data[0];
        Object.keys(firstRecord).slice(0, 10).forEach(key => {
          console.log(  `${key}:, firstRecord[key], (${typeof firstRecord[key]})`);
        });
      }

      // A newer fetch started while this one was in flight: its results are the
      // ones the user is waiting for, so drop these.
      if (requestId !== requestIdRef.current) return;

      // This attempt succeeded, so an earlier *fetch* failure no longer applies.
      // A run failure is left alone: it is about a different operation, and the
      // user has not seen it resolve.
      if (errorSourceRef.current !== 'run') {
        showError(null);
      }

      // Clean the data using enhanced cleaning function
      const cleanCashflowData = cleanDataForGrid(cashflowResponse?.data || []);
      const cleanMetricsData = cleanDataForGrid(metricsResponse?.data || []);

      console.log('After cleaning:');
      console.log('- Clean cashflow length:', cleanCashflowData.length);
      console.log('- Clean metrics length:', cleanMetricsData.length);

      // Force state updates with functional setState to ensure they trigger
      setCashflowData(prevData => {
        console.log('Setting cashflow data, prev length:', prevData?.length || 0, 'new length:', cleanCashflowData.length);
        return cleanCashflowData;
      });
      
      setMetricsData(prevData => {
        console.log('Setting metrics data, prev length:', prevData?.length || 0, 'new length:', cleanMetricsData.length);
        return cleanMetricsData;
      });

      // Add a small delay to ensure state has updated before logging
      setTimeout(() => {
        console.log('State should be updated now. Metrics data length in state should be:', cleanMetricsData.length);
      }, 100);
      
    } catch (error) {
      console.error('Error fetching past results:', error);
      // Same rule for failures: a superseded denial must not overwrite the
      // state of the request that replaced it.
      if (requestId !== requestIdRef.current) return;
      if (isPermissionDenied(error)) {
        // The Message variant, which inlines the Permissions-app URL: there is
        // no button here to replace that link with.
        showError(getPermissionDeniedMessage(error), 'fetch');
      } else {
        showError('Failed to fetch past results. Please try again.', 'fetch');
      }
      setCashflowData([]);
      setMetricsData([]);
    } finally {
      // Only the newest request owns the spinner; clearing it from a superseded
      // one would hide that a newer fetch is still running.
      if (requestId === requestIdRef.current) {
        setTableLoading(false);
      }
    }
  };

  // Set up polling for cashflow and metrics data
  useEffect(() => {
    if (currentConfigId) {
      setIsWaitingForConfig(true);
      setTableLoading(true);
      // Initial fetch
      fetchData();

      // Set up polling every 5 seconds
      pollingIntervalRef.current = setInterval(fetchData, 5000);

      // Cleanup on unmount or when configId changes
      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    }
  }, [currentConfigId]);

  const fetchData = async () => {
    if (!currentConfigId) return;

    try {
      const payload = {
        cusips: formData.cusips,
        configId: currentConfigId,
        scenarios: formData.scenarios,
        asofdt: formData.asofdt,
        env: formData.env
      };

      console.log('Polling fetchData with payload:', payload);
      const [cashflowResponse, metricsResponse] = await Promise.all([
        fetchCashflow(payload),
        fetchMetrics(payload)
      ]);

      const cashflowLength = cashflowResponse?.data?.length || 0;
      const metricsLength = metricsResponse?.data?.length || 0;
      
      console.log('Poll response - Cashflow:', cashflowLength, 'Metrics:', metricsLength);

      // Only update data if we actually received data
      if (cashflowLength > 0 || metricsLength > 0) {
        console.log('Updating data from polling');
        
        const cleanCashflowData = cleanDataForGrid(cashflowResponse?.data || []);
        const cleanMetricsData = cleanDataForGrid(metricsResponse?.data || []);

        setCashflowData(cleanCashflowData);
        setMetricsData(cleanMetricsData);
        
        console.log('Data updated via polling - Cashflow:', cleanCashflowData.length, 'Metrics:', cleanMetricsData.length);
        
        setTableLoading(false);
        setIsWaitingForConfig(false);
        
        // Clear polling once we have data
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      } else {
        console.log('No data received from polling, continuing to poll...');
      }
    } catch (error) {
      console.error('Error fetching data during polling:', error);
      notifyUngatedFailure(showNotification, error);
      setTableLoading(false);
      setIsWaitingForConfig(false);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }
  };

  const handleFormSubmit = async (configData) => {
    // This run owns the error state now. An automatic fetchPastResults already
    // in flight would otherwise land later and clear this run's failure with
    // its own showError(null).
    invalidateActiveFetch();
    setLoading(true);
    showError(null);
    
    // Clear existing data and show loading wheels immediately
    setCashflowData([]);
    setMetricsData([]);
    setTableLoading(true);
    
    try {
      if (metricsMode) {
        // Metrics Mode: Call /api/metrics instead of runScenario
        const payload = {
          cusips: formData.cusips,
          asofdt: formData.asofdt,
          scenarios: formData.scenarios,
          env: formData.env
        };
        
        console.log('Metrics Mode - calling /api/metrics with payload:', payload);
        const response = await fetchMetricsMode(payload);
        
        console.log('Metrics Mode API Response:', response?.data);
        
        // The API returns { cashflows, analytics }
        const cleanCashflowData = cleanDataForGrid(response?.data?.cashflows || []);
        const cleanMetricsData = cleanDataForGrid(response?.data?.analytics || []);

        console.log('After cleaning (metrics mode):');
        console.log('- Clean cashflow length:', cleanCashflowData.length);
        console.log('- Clean analytics length:', cleanMetricsData.length);

        setCashflowData(cleanCashflowData);
        setMetricsData(cleanMetricsData);
        setTableLoading(false);
      } else {
        // Standard mode: Run scenario and poll for results
        setIsWaitingForConfig(true);
        console.log('Submitting config data:', configData);
        const response = await runScenario(configData);
        setCurrentConfigId(response.data.configId);
      }
    } catch (error) {
      console.error('Error in Portfolio analysis:', error);
      // Only the standard branch calls a gated endpoint (run_scenario). The
      // metrics branch posts to /portfolio_analytics, which the server does not
      // implement — so a permission_denied cannot come from it, and offering to
      // request a role there would be meaningless.
      const notify = metricsMode ? notifyUngatedFailure : notifyPermissionError;
      if (!notify(showNotification, error)) {
        showError('Failed to run analysis. Please try again.', 'run');
      }
      // Reset loading states on error
      setTableLoading(false);
      setIsWaitingForConfig(false);
    } finally {
      setLoading(false);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  // Monitor metrics data changes with detailed logging
  useEffect(() => {
    console.log('=== METRICS DATA CHANGED IN MAIN ===');
    console.log('New metricsData:', metricsData);
    console.log('Type:', typeof metricsData);
    console.log('Is Array:', Array.isArray(metricsData));
    console.log('Length:', metricsData?.length);
    
    if (metricsData && metricsData.length > 0) {
      console.log('First record keys:', Object.keys(metricsData[0]));
      console.log('First record values sample:', {
        CUSIP: metricsData[0].CUSIP,
        CLEAN_PX: metricsData[0].CLEAN_PX,
        BOOK_VALUE: metricsData[0].BOOK_VALUE
      });
    }
  }, [metricsData]);

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3} direction="column">
        {/* Portfolio Analysis Toolbar */}
        <Grid item xs={12}>
          <PortfolioForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleFormSubmit}
            loading={loading}
            error={error}
            isFormComplete={isFormComplete}
            isWaitingForConfig={isWaitingForConfig}
            userId={userId}
            metricsMode={metricsMode}
            onMetricsModeChange={setMetricsMode}
            selectedLotIds={selectedLotIds}
            onLotIdsChange={setSelectedLotIds}
            availableLotIds={availableLotIds}
            hasData={cashflowData.length > 0 && metricsData.length > 0}
          />
        </Grid>

        {/* Cashflow Table */}
        <Grid item xs={12}>
          <CusipTables
            cashflowData={filteredCashflowData}
            metricsData={null} // Only show cashflow table
            tableLoading={tableLoading}
            formData={formData}
            showDebugInfo={true}
            tableType="cashflow"
          />
        </Grid>

        {/* Metrics Table */}
        <Grid item xs={12}>
          <CusipTables
            cashflowData={null} // Only show metrics table
            metricsData={filteredMetricsData}
            tableLoading={tableLoading}
            formData={formData}
            showDebugInfo={true}
            tableType="metrics"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PortfolioAnalysis;
